/** What a waiting pull request would publish, drawn, for whoever has to decide.
 *
 * The same map the public sees on a circuit's page — `TrackMap` — given a
 * different document: not what is published but what the job compiled for a
 * pull request it declined to merge on its own. Three things are added that
 * only this screen wants. The outline published today, ghosted behind, so
 * what the pull request adds and what it would take away are both visible.
 * The raw finish crossings as dots, because the gate judged how far apart
 * they are and the averaged finish line hides it. And a row of places to go:
 * each gap with its length, and the start/finish with its spread, so "right
 * 96 %, 4 gaps" becomes four places that can each be looked at in a click.
 *
 * A circuit with no candidate is ordinary, and the message says which of the
 * two ordinary reasons it is likely to be rather than reporting an error: no
 * pull request is waiting, or the one that is was opened before the job sent
 * these and its next run will.
 */

import { useEffect, useMemo, useState } from 'react';

import { ApiError, adminTrackCandidate } from '@/lib/api';
import { count, percent, relativeTime } from '@/lib/format';
import type { TrackCandidate } from '@/lib/types';
import { ErrorNote, Kicker, Spinner } from '@/components/ui';
import { TrackMap } from '@/components/tracks/TrackMap';
import type { MapFocus } from '@/components/tracks/TrackMap';
import { pullRequestLabel } from '@/components/tracks/list';
import {
  SIDE_NAME,
  coverageChange,
  crossingSpread,
  dotsPath,
  gapList,
  lookSpan,
  outlinePath,
} from '@/components/admin/candidate';

const MUTED = 'var(--color-neutral-500)';
const FAINT = 'var(--color-neutral-600)';
const GHOST = 'var(--color-neutral-600)';
const CROSSING = 'var(--color-ok)';

export interface CandidateMapProps {
  officialId: string;
  /** Changes when the screen wants the candidate read again. */
  refreshKey?: string | number;
  height?: number;
}

export function CandidateMap({ officialId, refreshKey, height = 380 }: CandidateMapProps) {
  const [candidate, setCandidate] = useState<TrackCandidate | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [looks, setLooks] = useState(0);
  const [ghost, setGhost] = useState(true);

  useEffect(() => {
    let live = true;
    setCandidate(null);
    setMissing(false);
    setError(null);
    setFocus(null);

    void adminTrackCandidate(officialId).then(
      (value) => {
        if (live) setCandidate(value);
      },
      (thrown: unknown) => {
        if (!live) return;
        if (thrown instanceof ApiError && thrown.status === 404) setMissing(true);
        else setError(thrown);
      },
    );
    return () => {
      live = false;
    };
  }, [officialId, refreshKey, attempt]);

  const gaps = useMemo(() => (candidate ? gapList(candidate.geometry) : []), [candidate]);
  const spread = useMemo(
    () => (candidate ? crossingSpread(candidate.finish_crossings) : null),
    [candidate],
  );
  const sides = useMemo(
    () => (candidate ? coverageChange(candidate.geometry, candidate.published) : []),
    [candidate],
  );
  const ghostPath = useMemo(() => outlinePath(candidate?.published ?? null), [candidate]);
  const crossingDots = useMemo(() => dotsPath(candidate?.finish_crossings ?? []), [candidate]);

  if (error) {
    return (
      <ErrorNote
        error={error}
        title="The pull request’s geometry could not be loaded."
        compact
        onRetry={() => setAttempt((value) => value + 1)}
      />
    );
  }

  if (missing) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, color: MUTED, textWrap: 'pretty' }}>
        The job has sent no geometry for a pull request on this circuit, so there is no map to
        draw. Most likely nothing is waiting any more: a pull request that was merged or closed by
        hand on GitHub goes on being listed here, because nothing tells the service — follow its
        link to see. Otherwise it was opened before the job started sending geometry, and its next
        run will.
      </p>
    );
  }

  if (!candidate) return <Spinner label="loading what the pull request would publish" />;

  const look = (x: number, z: number, span: number) => {
    setFocus({ x, z, span, key: looks + 1 });
    setLooks((value) => value + 1);
  };

  const pr = pullRequestLabel(candidate.pr_url);
  const status = candidate.merge_request?.status ?? null;
  const settled = status !== null && status !== 'awaiting_review' && status !== 'open';

  return (
    <section
      aria-label="What the pull request would publish"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <Kicker tone="muted">what {pr ?? 'the pull request'} would publish</Kicker>
        <span style={{ fontSize: 11.5, color: FAINT }}>
          compiled {relativeTime(candidate.stored_at)}
          {candidate.published ? '' : ' · nothing is published for this circuit yet'}
        </span>
        {candidate.pr_url ? (
          <a
            href={candidate.pr_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ marginLeft: 'auto', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            {pr ?? 'Pull request'}
            <span className="sr-only"> on GitHub</span> →
          </a>
        ) : null}
      </div>

      {settled ? (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--color-warn)' }}>
          This pull request is no longer waiting — the job last reported it as{' '}
          {String(status).replace(/_/g, ' ')}.
        </p>
      ) : null}

      <TrackMap
        geometry={candidate.geometry}
        title={`What ${pr ?? 'the pull request'} would publish for ${candidate.track_name || officialId}`}
        height={height}
        focus={focus}
      >
        {ghost && ghostPath ? (
          <path
            d={ghostPath}
            fill="none"
            stroke={GHOST}
            strokeWidth={1}
            strokeDasharray="5 4"
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: 'none' }}
          />
        ) : null}
        {crossingDots ? (
          <path
            d={crossingDots}
            fill="none"
            stroke={CROSSING}
            strokeWidth={7}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: 'none' }}
          />
        ) : null}
      </TrackMap>

      <div
        className="tabular-nums"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', fontSize: 12.5, color: MUTED }}
      >
        {sides.map((side) => (
          <span key={side.side}>
            {SIDE_NAME[side.side]}{' '}
            <span style={{ color: 'var(--color-text)' }}>{percent(side.now)}</span>
            {side.change !== null && side.change !== 0 ? (
              <span
                style={{ color: side.change < 0 ? 'var(--color-error)' : 'var(--color-ok)' }}
                title={`published today: ${percent(side.was)}`}
              >
                {' '}
                {side.change > 0 ? '+' : '−'}
                {Math.abs(side.change).toFixed(1)} pts
              </span>
            ) : side.was !== null ? (
              <span> · unchanged</span>
            ) : null}
          </span>
        ))}
        {ghostPath ? (
          <label style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={ghost}
              onChange={(event) => setGhost(event.currentTarget.checked)}
            />
            <span
              aria-hidden="true"
              style={{ width: 16, borderTop: `1px dashed ${GHOST}`, display: 'inline-block' }}
            />
            published today
          </label>
        ) : null}
      </div>

      <div role="group" aria-label="Places to look at" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 12, color: FAINT, alignSelf: 'center' }}>look at</span>
        {spread ? (
          <button
            type="button"
            className="btn btn-ghost tabular-nums"
            style={{ fontSize: 12, padding: '3px 9px', gap: 7 }}
            title="The dots are where laps were recorded rolling over; the gate measures the furthest two apart."
            onClick={() => look(spread.at.x, spread.at.z, lookSpan(spread.spread_m))}
          >
            <span
              aria-hidden="true"
              style={{ width: 8, height: 8, borderRadius: '50%', background: CROSSING, flex: 'none' }}
            />
            start / finish · {count(spread.count)} crossings, {spread.spread_m.toFixed(1)} m apart
          </button>
        ) : null}
        {gaps.map((gap) => (
          <button
            key={gap.id}
            type="button"
            className="btn btn-ghost tabular-nums"
            style={{ fontSize: 12, padding: '3px 9px', gap: 7 }}
            onClick={() => look(gap.at.x, gap.at.z, lookSpan(gap.length_m))}
          >
            <span
              aria-hidden="true"
              style={{ width: 10, borderTop: '2px dotted var(--color-warn)', flex: 'none' }}
            />
            {SIDE_NAME[gap.side]} gap · {gap.length_m.toFixed(0)} m
          </button>
        ))}
        {gaps.length === 0 ? (
          <span style={{ fontSize: 12, color: MUTED, alignSelf: 'center' }}>no gaps on either side</span>
        ) : null}
      </div>
    </section>
  );
}

export default CandidateMap;
